import { create } from 'zustand';
import {
  FILTER_CONFIGS,
  emptyCompanyDomains,
  type CompanyDomains,
  type IntentTopicSelection,
  type TechnographicSelection,
  type ListKind,
  type ListType,
  type UploadedFile,
  type FilterSelections,
  type SelectedFilterValue,
} from '@/app/audience-builder/lib';
import {
  buildGeoLookups,
  syncRegionChange,
  syncCountryChange,
  hasUnitedStates,
  syncStateOnCountryChange,
} from '@/app/audience-builder/utils/geo-sync';
import { buildIndustryLookups, syncIndustryChange } from '@/lib/industry';
import type { ContentFile, ContentLink, ContentProductKind } from '@/app/audience-content/lib';
import type { PlayKey } from '@/components/shared/profile';
import type { SignalSourceKey } from '@/components/shared/signal-sources';
import { useAudienceBuilderUiStore } from './audience-builder-ui-store';

const emptyFilters = (): FilterSelections =>
  Object.fromEntries(FILTER_CONFIGS.map((f) => [f.key, []])) as FilterSelections;

const GEO_KEYS = new Set(['geoRegion', 'country']);

/**
 * Comparison key for "is this value already on the filter?" (NPJB-608 AC6).
 *
 * Shared by both custom-add paths so they cannot drift on trimming or casing — the
 * single-value `addCustomOption` and the batch `addCustomOptions` must agree, or a
 * value the dropdown rejects as a duplicate could still arrive through the batch.
 */
const dedupeKey = (label: string): string => label.trim().toLowerCase();

const alreadySelected = (current: readonly SelectedFilterValue[], label: string): boolean =>
  current.some((v) => dedupeKey(v.value) === dedupeKey(label));

/**
 * NPJB-559 — keep the State selection consistent with Country, in both directions.
 *
 * State may hold values only while "United States" is selected (AC3), so every write
 * that can drop the U.S. must clear it (AC5) — and every write that can re-add the
 * U.S. must restore what was cleared (AC9). There are **five** such writes, which is
 * why this is a helper rather than inline logic:
 *
 *   1. `setFilterValues('country', …)` plain path (no geo hierarchy loaded)
 *   2. `removeFilterValue('country', …)` plain path — the chip X
 *   3. `setGeoFilterValues` fallback branch (no hierarchy)
 *   4. `setGeoFilterValues` hierarchy branch — note this also fires for a *Region*
 *      change, because `syncRegionChange` adds/removes that region's countries
 *   5. `clearFilter` geo branch — clearing **Region** wipes `country` too, so it can
 *      drop the U.S. without the user touching Country at all
 *
 * Returns the caller's own `filters` reference untouched when nothing changed, so no
 * write triggers a needless re-render.
 */
const reconcileStateWithCountry = (
  state: AudienceState,
  filters: FilterSelections,
): { filters: FilterSelections; clearedStates: ClearedStateMemory | null } => {
  const unchanged = { filters, clearedStates: state.clearedStates };
  const countries = filters.country ?? [];
  const currentStates = filters.state ?? [];

  if (hasUnitedStates(countries)) {
    // AC9 — the U.S. is back. Restore only into an empty field (never clobber a live
    // selection) and only for this draft, so audience B can't inherit audience A's.
    const memory = state.clearedStates;
    if (currentStates.length > 0 || !memory || memory.values.length === 0) return unchanged;
    if (memory.draftId !== state.draftId) return unchanged;
    // Consumed on restore: a later manual clear must not be silently undone.
    return { filters: { ...filters, state: memory.values }, clearedStates: null };
  }

  const { state: nextStates, removed } = syncStateOnCountryChange(countries, currentStates);
  if (removed.length === 0) return unchanged;
  return {
    filters: { ...filters, state: nextStates },
    clearedStates: { draftId: state.draftId, values: removed },
  };
};

export type AudienceRequestStatus =
  | 'draft'
  | 'buildingAudience'
  | 'availsReady'
  | 'activating'
  | 'campaignActive'
  | 'delivered'
  | 'paused'
  | 'archived';

export interface AudienceTargetSlice {
  brief: string;
  name: string | null;
  files: UploadedFile[];
  filters: FilterSelections;
  leadGoal: number | null;
  leadsPerCompany: { name: number | string; isCustom: boolean } | null;
  campaignTimeframe: { name: string; isCustom: boolean } | null;
  /**
   * NPJB-552 — campaign timeline as two independent ISO `YYYY-MM-DD` strings.
   * Strings (not Dayjs) keep the localStorage draft mirror JSON-serializable.
   * Either may be set without the other while the user is mid-draft.
   */
  startDate: string | null;
  endDate: string | null;
  impressionGoal: number | null;
  additionalDetails: string;
  /** NPJB-539 — free-text Salesforce deal reference (internal-set). Null = not set. */
  dealReference: string | null;
  intentTopicSelections: IntentTopicSelection[];
  /** Selected technologies. Its own list, never merged into intentTopicSelections — the
   *  backend persists them in a separate column for the same reason. */
  technographicSelections: TechnographicSelection[];
  /**
   * NPJB-764 — the signal sources the audience listens to. `[]` means all of them, the
   * same rule an Intelligence audience uses. Set through the shared Signal sources dialog.
   */
  signalSources: SignalSourceKey[];
  /** NPJB-763 — the play activated in Intelligence. `null` for a whole audience. */
  play: PlayKey | null;
  /** NPJB-763 — the accounts that play resolved to. Always present, empty when none. */
  companyDomains: CompanyDomains;
}

/**
 * Per-tab flags marking that an accordion holds typed-but-not-yet-Saved input
 * (NPJB-425, Option B). The Content page reads these on Continue/Back to warn
 * about unsaved content. NOT part of the draft — draft-store-sync serializes an
 * explicit key list, so this is naturally excluded from autosave.
 */
export interface UnsavedContentInput {
  link: boolean;
  upload: boolean;
  product: boolean;
}

export interface AudienceContentSlice {
  files: ContentFile[];
  links: ContentLink[];
  requestedProducts: ContentProductKind[];
  unsavedInput: UnsavedContentInput;
}

export interface SelectedProduct {
  id: string;
  name: string;
  category: string;
}

export type ProductAnswerValue = string | string[] | null;
export type ProductAnswers = Record<string, ProductAnswerValue>;

export interface AudienceProductsSlice {
  selectedProducts: SelectedProduct[];
  budget: number | null;
  productAnswers: Record<string, ProductAnswers>;
}

/**
 * NPJB-559 — states dropped because "United States" left the Country selection,
 * kept so re-adding the U.S. restores them without the user reselecting (AC8/AC9).
 *
 * Lives at the top level of this store rather than inside `target`, which is what
 * makes it client-only: `draftFromStore` serializes an explicit key list from
 * `target`/`content`/`products`, so nothing here reaches localStorage or the server
 * (AC8). It is a module-scoped singleton, so it survives component remount and route
 * changes but starts empty on a page reload (AC10).
 *
 * Keyed by `draftId` so audience B can never restore audience A's states.
 */
interface ClearedStateMemory {
  draftId: string | null;
  values: SelectedFilterValue[];
}

interface AudienceState {
  draftId: string | null;
  lastSeenUpdatedAt: string | null;
  status: AudienceRequestStatus | null;
  campaignId: string | null;
  draftFollowing: boolean;
  clearedStates: ClearedStateMemory | null;

  target: AudienceTargetSlice;
  content: AudienceContentSlice;
  products: AudienceProductsSlice;

  // True right after an Explore→Target handoff; drives the pre-fill banner (NPJB-308).
  // Transient (store isn't persisted), so it never shows on reload or an existing draft.
  prefilledFromExplore: boolean;

  /**
   * What the Explore→Target hand-off brought with it (NPJB-725 AC4/AC5/AC7).
   *
   * The banner used to say only "filters carried over". AC5 wants the seller to see
   * WHICH ones, one line each, so they can judge the Accept rather than take it on
   * trust — and AC4 wants the audience named when one was open.
   */
  explorePrefill: {
    /** The open audience's name, or `null` when the rail was not a saved audience. */
    audienceName: string | null;
    /** One line per thing that arrived selected, e.g. "Industry: Banking, Retail". */
    carried: string[];
    /**
     * Carried, but targeting has no field for it (AC7).
     *
     * Empty today: the builder offers every filter Explore does. It fills when NPJB-661
     * ships "the technology an account does NOT use", which has no counterpart here —
     * and must never be folded into "uses", because that inverts what was asked for.
     */
    carriedNotSet: string[];
  };

  // Target setters
  setBrief: (brief: string) => void;
  setName: (name: string | null) => void;
  setDealReference: (dealReference: string | null) => void;
  applySuggestedName: (suggested: string | null) => void;
  setAdditionalDetails: (additionalDetails: string) => void;
  addFiles: (newFiles: UploadedFile[]) => void;
  removeFile: (uid: string) => void;
  setFileListType: (uid: string, listType: ListType | null) => void;
  setFileListKind: (uid: string, listKind: ListKind | null) => void;
  updateFileAfterUpload: (
    uid: string,
    data: { id: string; name: string; fileKey: string; rowCount?: number },
  ) => void;
  updateFilesAfterUpload: (
    updates: {
      uid: string;
      data: { id: string; name: string; fileKey: string; rowCount?: number };
    }[],
  ) => void;
  setFileError: (uid: string, error: string) => void;
  setFilesError: (uids: string[], error: string) => void;
  setFilters: (filters: FilterSelections) => void;
  setFiles: (files: UploadedFile[]) => void;
  setFilterValues: (filterKey: string, values: SelectedFilterValue[]) => void;
  removeFilterValue: (filterKey: string, value: string) => void;
  addCustomOption: (filterKey: string, label: string) => void;
  /**
   * Append several custom values in one write (NPJB-553 — a pasted job-title list).
   * One markEdited() and one state update instead of N, and de-duplicates against
   * what's already selected. Callers pass values already trimmed/validated.
   */
  addCustomOptions: (filterKey: string, labels: readonly string[]) => void;
  clearFilter: (filterKey: string) => void;
  clearAllFilters: () => void;
  applyParsedFilters: (mapped: Record<string, string[]>) => void;

  // Explore→Target pre-fill banner (NPJB-308): flag setter + accept (persist) + deny (clear).
  setCampaignId: (id: string | null) => void;

  // Explore→Target pre-fill banner (NPJB-308): flag setter + deny (clears filters+brief).
  setPrefilledFromExplore: (value: boolean) => void;
  setExplorePrefill: (summary: AudienceState['explorePrefill']) => void;
  acceptExplorePrefill: () => void;
  denyExplorePrefill: () => void;
  setLeadGoal: (leadGoal: number | null) => void;
  setLeadsPerCompany: (
    leadsPerCompany: { name: number | string; isCustom: boolean } | null,
  ) => void;
  setCampaignTimeframe: (campaignTimeframe: { name: string; isCustom: boolean } | null) => void;
  /** NPJB-552 — ISO `YYYY-MM-DD`, or null to clear. Independent of `setEndDate`. */
  setStartDate: (startDate: string | null) => void;
  setEndDate: (endDate: string | null) => void;
  setImpressionGoal: (impressionGoal: number | null) => void;
  setGeoFilterValues: (changedKey: 'geoRegion' | 'country', values: SelectedFilterValue[]) => void;
  setIndustryFilterValues: (values: SelectedFilterValue[]) => void;

  // Intent topic setters
  setTechnographics: (selections: TechnographicSelection[]) => void;
  /** NPJB-764 — the Signal sources dialog's answer. `[]` means all sources. */
  setSignalSources: (sources: SignalSourceKey[]) => void;
  /** NPJB-763 — a hand edit. Keeps where the list came from, so Refresh still works. */
  setCompanyDomains: (domains: string[]) => void;
  /** NPJB-763 — the play hand-off and Refresh: the play and a freshly resolved list. */
  carryPlay: (play: PlayKey | null, companyDomains: CompanyDomains) => void;
  /** The Play field's own edit. Company Domains is untouched: it changes only on Refresh. */
  setPlay: (play: PlayKey | null) => void;
  setTechnographicMode: (topicId: string, mode: TechnographicSelection['mode']) => void;
  removeTechnographic: (topicId: string) => void;
  setIntentTopics: (selections: IntentTopicSelection[]) => void;
  addIntentTopic: (topic: IntentTopicSelection) => void;
  removeIntentTopic: (topicId: string) => void;
  removeIntentTopicsBySubcategory: (category: string, subcategory: string) => void;
  removeIntentTopicsByCategory: (category: string) => void;
  clearIntentTopics: () => void;

  // Products setters
  toggleProduct: (product: SelectedProduct) => void;
  clearProducts: () => void;
  setBudget: (value: number | null) => void;
  setSelectedProducts: (products: SelectedProduct[]) => void;
  setProductAnswer: (productId: string, key: string, value: ProductAnswerValue) => void;
  setProductAnswers: (productAnswers: Record<string, ProductAnswers>) => void;
  clearProductAnswers: (productId: string) => void;
  clearAllProductAnswers: () => void;

  // Content setters
  addContentFile: (file: ContentFile) => void;
  updateContentFile: (id: string, patch: Partial<Omit<ContentFile, 'id'>>) => void;
  removeContentFile: (id: string) => void;
  addContentLink: (link: Omit<ContentLink, 'id'>) => void;
  updateContentLink: (id: string, patch: Partial<Omit<ContentLink, 'id'>>) => void;
  removeContentLink: (id: string) => void;
  setContentLinks: (links: ContentLink[]) => void;
  setContentFiles: (files: ContentFile[]) => void;
  setRequestedContentProducts: (products: ContentProductKind[]) => void;
  removeRequestedContentProduct: (kind: ContentProductKind) => void;
  setUnsavedLink: (dirty: boolean) => void;
  setUnsavedUpload: (dirty: boolean) => void;
  setUnsavedProduct: (dirty: boolean) => void;
  clearUnsavedContent: () => void;

  // Identity / sync
  setDraftIdentity: (input: {
    draftId: string;
    lastSeenUpdatedAt: string;
    status: AudienceRequestStatus;
    following: boolean;
  }) => void;
  setLastSeenUpdatedAt: (updatedAt: string) => void;
  setDraftFollowing: (following: boolean) => void;
  clearDraftIdentity: () => void;

  clearAll: () => void;
  reset: () => void;
  /** Reset identity + content + products, preserving target (for explore handoff). */
  resetKeepingTarget: () => void;
}

export const emptyTarget = (): AudienceTargetSlice => ({
  brief: '',
  name: null,
  files: [],
  filters: emptyFilters(),
  leadGoal: null,
  leadsPerCompany: null,
  campaignTimeframe: null,
  startDate: null,
  endDate: null,
  impressionGoal: null,
  additionalDetails: '',
  dealReference: null,
  intentTopicSelections: [],
  technographicSelections: [],
  signalSources: [],
  play: null,
  companyDomains: emptyCompanyDomains(),
});

const emptyContent = (): AudienceContentSlice => ({
  files: [],
  links: [],
  requestedProducts: [],
  unsavedInput: { link: false, upload: false, product: false },
});

const emptyProducts = (): AudienceProductsSlice => ({
  selectedProducts: [],
  budget: null,
  productAnswers: {},
});

const markEdited = () => useAudienceBuilderUiStore.getState().markUserEdited();

/** Numeric goal answer keys that aggregate into target.leadGoal / target.impressionGoal. */
const LEAD_GOAL_KEYS = new Set(['lead-goal', 'a-lead-goal']);
const IMPRESSION_GOAL_KEYS = new Set(['impression-goal']);

/**
 * All per-product answer keys that represent goals. These aggregate into
 * target.leadGoal / target.impressionGoal (and render in the Goals card), so
 * human-readable product recaps skip them to avoid duplication. Single source
 * of truth for both the aggregation above and `resolveProductDetails`.
 */
export const GOAL_ANSWER_FIELD_KEYS: ReadonlySet<string> = new Set([
  ...LEAD_GOAL_KEYS,
  ...IMPRESSION_GOAL_KEYS,
]);

/**
 * NPJB-791 — a goal of 0 or less means "no goal". The API rejects it, and the
 * push guard treats null as no goal, so it is stored as null.
 */
const positiveGoalOrNull = (goal: number | null): number | null =>
  goal !== null && goal > 0 ? goal : null;

/** A goal answer counts only when it is a string with more than spaces in it. */
const isFilledGoalAnswer = (v: ProductAnswerValue | undefined): v is string =>
  typeof v === 'string' && v.trim().length > 0;

/**
 * Sum the goal answers under `keys`. An empty answer, or a sum of 0 or less,
 * gives null (see `positiveGoalOrNull`).
 */
const sumGoal = (answers: Record<string, ProductAnswers>, keys: Set<string>): number | null => {
  let total = 0;
  for (const productAnswers of Object.values(answers)) {
    for (const [k, v] of Object.entries(productAnswers)) {
      if (!keys.has(k) || !isFilledGoalAnswer(v)) continue;
      const n = Number(v);
      if (Number.isFinite(n)) total += n;
    }
  }
  return positiveGoalOrNull(total);
};

/** Decides if one goal type (its answer `keys`) is recomputed from the answers. */
type GoalRecompute = (keys: Set<string>) => boolean;

/** A missing, null or blank answer all mean "empty", so a move between them is no change. */
const goalAnswerOf = (v: ProductAnswerValue | undefined): ProductAnswerValue =>
  isFilledGoalAnswer(v) || Array.isArray(v) ? v : null;

/** True for the goal type of `key` when its answer moved from `prev` to `next`. */
const goalAnswerChanged =
  (key: string, prev: ProductAnswerValue | undefined, next: ProductAnswerValue): GoalRecompute =>
  (keys) =>
    keys.has(key) && goalAnswerOf(prev) !== goalAnswerOf(next);

/** True when some product holds a non-empty answer under `keys`. */
const goalAnswersFilled =
  (answers: Record<string, ProductAnswers>): GoalRecompute =>
  (keys) =>
    Object.values(answers).some((productAnswers) =>
      [...keys].some((k) => isFilledGoalAnswer(productAnswers[k])),
    );

/**
 * Recompute target.leadGoal / target.impressionGoal as the sum of matching
 * per-product answers, so the Review step (which still reads target.*) stays
 * in sync with values typed inside the Products step configurators. Returns
 * the partial-state Zustand expects from `set()`.
 *
 * Only the goal types `recompute` picks are summed. The other keeps its value,
 * so editing one answer cannot wipe a goal the AI brief set.
 */
const aggregateGoalsInto = (
  state: AudienceState,
  nextAnswers: Record<string, ProductAnswers>,
  recompute: GoalRecompute,
): Pick<AudienceState, 'target' | 'products'> => {
  const goalFor = (keys: Set<string>, current: number | null) =>
    recompute(keys) ? sumGoal(nextAnswers, keys) : current;
  return {
    target: {
      ...state.target,
      leadGoal: goalFor(LEAD_GOAL_KEYS, state.target.leadGoal),
      impressionGoal: goalFor(IMPRESSION_GOAL_KEYS, state.target.impressionGoal),
    },
    products: {
      ...state.products,
      productAnswers: nextAnswers,
    },
  };
};

const cryptoRandomId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// Merge one tab's draft into the per-tab pending object without touching the
// other tab, collapsing to null once both slots are empty. `patch` carries the
// changed slot (link OR upload); passing `null`/undefined clears just that slot.
export const useAudienceStore = create<AudienceState>((set, get) => ({
  draftId: null,
  lastSeenUpdatedAt: null,
  status: null,
  campaignId: null,
  draftFollowing: false,
  clearedStates: null,
  target: emptyTarget(),
  content: emptyContent(),
  products: emptyProducts(),
  prefilledFromExplore: false,
  explorePrefill: { audienceName: null, carried: [], carriedNotSet: [] },

  setBrief: (brief) => {
    markEdited();
    set((state) => ({ target: { ...state.target, brief } }));
  },
  setName: (name) => {
    if (get().target.name === name) return;
    markEdited();
    set((state) => ({ target: { ...state.target, name } }));
  },
  applySuggestedName: (suggested) => {
    if (suggested === null || suggested === undefined) return;
    const current = get().target.name;
    if (current !== null && current.trim() !== '') return;
    get().setName(suggested);
  },
  setAdditionalDetails: (additionalDetails) => {
    markEdited();
    set((state) => ({ target: { ...state.target, additionalDetails } }));
  },
  setDealReference: (dealReference) => {
    if (get().target.dealReference === dealReference) return;
    markEdited();
    set((state) => ({ target: { ...state.target, dealReference } }));
  },

  addFiles: (newFiles) => {
    markEdited();
    set((state) => {
      const files = [...state.target.files, ...newFiles];
      return { target: { ...state.target, files } };
    });
  },
  removeFile: (uid) => {
    markEdited();
    set((state) => {
      const files = state.target.files.filter((f) => f.uid !== uid);
      return { target: { ...state.target, files } };
    });
  },
  setFileListType: (uid, listType) => {
    markEdited();
    set((state) => {
      const files = state.target.files.map((f) => (f.uid === uid ? { ...f, listType } : f));
      return { target: { ...state.target, files } };
    });
  },
  setFileListKind: (uid, listKind) => {
    markEdited();
    set((state) => {
      const files = state.target.files.map((f) => (f.uid === uid ? { ...f, listKind } : f));
      return { target: { ...state.target, files } };
    });
  },
  updateFileAfterUpload: (uid, data) =>
    set((state) => {
      const files = state.target.files.map((f) =>
        f.uid === uid
          ? {
              ...f,
              id: data.id,
              name: data.name,
              fileKey: data.fileKey,
              rowCount: data.rowCount,
              status: 'done' as const,
            }
          : f,
      );
      return { target: { ...state.target, files } };
    }),
  updateFilesAfterUpload: (updates) =>
    set((state) => {
      const map = new Map(updates.map((u) => [u.uid, u.data]));
      const files = state.target.files.map((f) => {
        const data = map.get(f.uid);
        return data
          ? {
              ...f,
              id: data.id,
              name: data.name,
              fileKey: data.fileKey,
              rowCount: data.rowCount,
              status: 'done' as const,
            }
          : f;
      });
      return { target: { ...state.target, files } };
    }),
  setFileError: (uid, error) => {
    markEdited();
    set((state) => {
      const files = state.target.files.map((f) =>
        f.uid === uid ? { ...f, status: 'error' as const, error } : f,
      );
      return { target: { ...state.target, files } };
    });
  },
  setFilesError: (uids, error) => {
    markEdited();
    set((state) => {
      const uidSet = new Set(uids);
      const files = state.target.files.map((f) =>
        uidSet.has(f.uid) ? { ...f, status: 'error' as const, error } : f,
      );
      return { target: { ...state.target, files } };
    });
  },

  setFilters: (filters) => {
    markEdited();
    set((state) => ({
      // Merged over emptyFilters() so EVERY configured key is always present, even
      // when the source only carries some. hydrateStoreFrom feeds this from
      // deserializeFilters(wire.filters), which only contains keys the server
      // returned — so an audience saved before a filter existed would otherwise
      // leave that key undefined, and addCustomOption/removeFilterValue spread it
      // unguarded (TypeError). NPJB-553 hit this: every pre-existing audience
      // predates `jobTitle`. Keeps future filter additions safe too.
      target: { ...state.target, filters: { ...emptyFilters(), ...filters } },
    }));
  },
  setFiles: (files) => {
    markEdited();
    set((state) => ({
      target: {
        ...state.target,
        files,
      },
    }));
  },

  setFilterValues: (filterKey, values) => {
    markEdited();
    if (GEO_KEYS.has(filterKey) && useAudienceBuilderUiStore.getState().geoHierarchy) {
      get().setGeoFilterValues(filterKey as 'geoRegion' | 'country', values);
      return;
    }
    if (filterKey === 'industry') {
      get().setIndustryFilterValues(values);
      return;
    }
    set((state) => {
      // Site 1 — country reached here only when no geo hierarchy is loaded.
      const next = reconcileStateWithCountry(state, {
        ...state.target.filters,
        [filterKey]: values,
      });
      return {
        target: { ...state.target, filters: next.filters },
        clearedStates: next.clearedStates,
      };
    });
  },

  removeFilterValue: (filterKey, value) => {
    markEdited();
    if (GEO_KEYS.has(filterKey) && useAudienceBuilderUiStore.getState().geoHierarchy) {
      const current = get().target.filters[filterKey].filter((v) => v.value !== value);
      get().setGeoFilterValues(filterKey as 'geoRegion' | 'country', current);
      return;
    }
    if (filterKey === 'industry') {
      const current = (get().target.filters.industry ?? []).filter((v) => v.value !== value);
      get().setIndustryFilterValues(current);
      return;
    }
    set((state) => {
      // Site 2 — the chip X on Country when no geo hierarchy is loaded.
      const next = reconcileStateWithCountry(state, {
        ...state.target.filters,
        [filterKey]: (state.target.filters[filterKey] ?? []).filter((v) => v.value !== value),
      });
      return {
        target: { ...state.target, filters: next.filters },
        clearedStates: next.clearedStates,
      };
    });
  },

  addCustomOption: (filterKey, label) => {
    const current = get().target.filters[filterKey] ?? [];
    // Guard before markEdited(): a refused add must not dirty the draft or trigger an
    // autosave. The dropdown validates first and shows the reason (NPJB-608 AC6); this is
    // the backstop for every other caller.
    if (alreadySelected(current, label)) return;
    markEdited();
    set((state) => ({
      target: {
        ...state.target,
        filters: {
          ...state.target.filters,
          [filterKey]: [
            ...(state.target.filters[filterKey] ?? []),
            { id: null, label, value: label, type: 'custom' as const },
          ],
        },
      },
    }));
  },

  addCustomOptions: (filterKey, labels) => {
    if (labels.length === 0) return;
    markEdited();
    set((state) => {
      const current = state.target.filters[filterKey] ?? [];
      const seen = new Set(current.map((v) => dedupeKey(v.value)));
      const added = labels
        .filter((label) => {
          const key = dedupeKey(label);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((label) => ({ id: null, label, value: label, type: 'custom' as const }));
      if (added.length === 0) return state;
      return {
        target: {
          ...state.target,
          filters: { ...state.target.filters, [filterKey]: [...current, ...added] },
        },
      };
    });
  },

  clearFilter: (filterKey) => {
    markEdited();
    if (GEO_KEYS.has(filterKey)) {
      set((state) => {
        // Site 5 — this branch clears BOTH geo keys, so "Clear" on *Region* drops the
        // U.S. and must take State with it, even though the user never touched
        // Country. Easiest AC5 path to miss.
        const next = reconcileStateWithCountry(state, {
          ...state.target.filters,
          geoRegion: [],
          country: [],
        });
        return {
          target: { ...state.target, filters: next.filters },
          clearedStates: next.clearedStates,
        };
      });
      return;
    }
    // Clearing the parent industry also clears its sub-industries.
    if (filterKey === 'industry') {
      set((state) => ({
        target: {
          ...state.target,
          filters: { ...state.target.filters, industry: [], subIndustry: [] },
        },
      }));
      return;
    }
    set((state) => ({
      target: { ...state.target, filters: { ...state.target.filters, [filterKey]: [] } },
    }));
  },

  clearAllFilters: () => {
    markEdited();
    set((state) => ({ target: { ...state.target, filters: emptyFilters() } }));
  },

  setCampaignId: (id) => set({ campaignId: id }),
  setPrefilledFromExplore: (value) => set({ prefilledFromExplore: value }),
  setExplorePrefill: (summary) => set({ explorePrefill: summary }),

  // Accept on the Explore→Target banner: the user confirms intent to build from the
  // handoff. Keep the pre-filled filters, dismiss the banner, and mark edited FIRST so
  // the subscriber — which fires on the `set` below with hasUserEdited already true —
  // persists the carried-over state as a draft (same pattern as a real edit). This is
  // why a draft is created on confirm, but never on mere landing (handoff clears the
  // flag) or on Deny (which intentionally omits markEdited).
  acceptExplorePrefill: () => {
    markEdited();
    set({
      prefilledFromExplore: false,
      explorePrefill: { audienceName: null, carried: [], carriedNotSet: [] },
    });
  },

  // Deny on the Explore→Target banner: empty the pre-filled filters + brief + the
  // carried-over TAL files (Target only — the Explore session is untouched) and dismiss
  // the banner. Does NOT mark edited, so denying a pre-fill never autosaves / creates a
  // draft.
  denyExplorePrefill: () => {
    set((state) => ({
      target: {
        ...state.target,
        filters: emptyFilters(),
        brief: '',
        files: [],
        // NPJB-725 AC6: Deny clears EVERY pre-filled filter. Signal topics arrive with
        // the hand-off and land in Advanced Filters → Intent, so leaving them behind
        // left the seller with a targeting they explicitly declined.
        intentTopicSelections: [],
        // NPJB-764: technologies and sources arrive with the hand-off too.
        technographicSelections: [],
        signalSources: [],
        // NPJB-763: the play and its accounts arrive with the hand-off too.
        play: null,
        companyDomains: emptyCompanyDomains(),
      },
      prefilledFromExplore: false,
      explorePrefill: { audienceName: null, carried: [], carriedNotSet: [] },
    }));
  },

  applyParsedFilters: (mapped) => {
    markEdited();
    set((state) => {
      const filters = emptyFilters();
      for (const [key, values] of Object.entries(mapped)) {
        if (key in filters) {
          filters[key] = values.map((v) => ({
            id: null,
            label: v,
            value: v,
            type: 'standard' as const,
          }));
        }
      }
      return { target: { ...state.target, filters } };
    });
  },

  setLeadGoal: (leadGoal) => {
    markEdited();
    set((state) => ({ target: { ...state.target, leadGoal: positiveGoalOrNull(leadGoal) } }));
  },
  setLeadsPerCompany: (leadsPerCompany) => {
    markEdited();
    set((state) => ({ target: { ...state.target, leadsPerCompany } }));
  },
  setCampaignTimeframe: (campaignTimeframe) => {
    markEdited();
    set((state) => ({ target: { ...state.target, campaignTimeframe } }));
  },
  setStartDate: (startDate) => {
    if (get().target.startDate === startDate) return;
    markEdited();
    set((state) => ({ target: { ...state.target, startDate } }));
  },
  setEndDate: (endDate) => {
    if (get().target.endDate === endDate) return;
    markEdited();
    set((state) => ({ target: { ...state.target, endDate } }));
  },
  setImpressionGoal: (impressionGoal) => {
    markEdited();
    set((state) => ({
      target: { ...state.target, impressionGoal: positiveGoalOrNull(impressionGoal) },
    }));
  },

  setGeoFilterValues: (changedKey, values) => {
    markEdited();
    const hierarchy = useAudienceBuilderUiStore.getState().geoHierarchy;
    if (!hierarchy) {
      set((state) => {
        // Site 3 — no hierarchy, so the change applies to one key only.
        const next = reconcileStateWithCountry(state, {
          ...state.target.filters,
          [changedKey]: values,
        });
        return {
          target: { ...state.target, filters: next.filters },
          clearedStates: next.clearedStates,
        };
      });
      return;
    }

    const lookups = buildGeoLookups(hierarchy);
    const result =
      changedKey === 'geoRegion'
        ? syncRegionChange(
            values,
            get().target.filters.geoRegion,
            get().target.filters.country,
            lookups,
          )
        : syncCountryChange(values, hierarchy, lookups);

    set((state) => {
      // Site 4 — fires for a Region change too: syncRegionChange adds/removes that
      // region's countries, which can drop or restore the U.S. without Country being
      // touched directly.
      const next = reconcileStateWithCountry(state, {
        ...state.target.filters,
        geoRegion: result.geoRegion,
        country: result.country,
      });
      return {
        target: { ...state.target, filters: next.filters },
        clearedStates: next.clearedStates,
      };
    });
  },

  // Industry parent change (NPJB-334): prune sub-industries whose parent is no
  // longer selected. We never auto-add children — parent-only means "all of them",
  // expanded server-side.
  setIndustryFilterValues: (values) => {
    markEdited();
    const hierarchy = useAudienceBuilderUiStore.getState().industryHierarchy;
    if (!hierarchy) {
      set((state) => ({
        target: { ...state.target, filters: { ...state.target.filters, industry: values } },
      }));
      return;
    }
    const synced = syncIndustryChange(
      values,
      get().target.filters.subIndustry ?? [],
      buildIndustryLookups(hierarchy),
    );
    set((state) => ({
      target: {
        ...state.target,
        filters: {
          ...state.target.filters,
          industry: synced.industry,
          subIndustry: synced.subIndustry,
        },
      },
    }));
  },

  // Technographic actions. Each calls markEdited() for the same reason the intent actions do:
  // that is what flags the draft dirty so the autosave PATCH fires. Without it a picked
  // technology would sit in memory and never reach the server.
  setCompanyDomains: (domains) => {
    markEdited();
    set((state) => ({
      target: {
        ...state.target,
        companyDomains: { ...state.target.companyDomains, domains },
      },
    }));
  },
  carryPlay: (play, companyDomains) => {
    markEdited();
    set((state) => ({ target: { ...state.target, play, companyDomains } }));
  },
  setPlay: (play) => {
    markEdited();
    set((state) => ({ target: { ...state.target, play } }));
  },
  setTechnographics: (selections) => {
    markEdited();
    set((state) => ({ target: { ...state.target, technographicSelections: selections } }));
  },
  setSignalSources: (sources) => {
    markEdited();
    set((state) => ({ target: { ...state.target, signalSources: sources } }));
  },
  setTechnographicMode: (topicId, mode) => {
    markEdited();
    set((state) => ({
      target: {
        ...state.target,
        technographicSelections: state.target.technographicSelections.map((t) =>
          t.topicId === topicId ? { ...t, mode } : t,
        ),
      },
    }));
  },
  removeTechnographic: (topicId) => {
    markEdited();
    set((state) => ({
      target: {
        ...state.target,
        technographicSelections: state.target.technographicSelections.filter(
          (t) => t.topicId !== topicId,
        ),
      },
    }));
  },

  // Intent topic actions
  setIntentTopics: (selections) => {
    markEdited();
    set((state) => ({ target: { ...state.target, intentTopicSelections: selections } }));
  },
  addIntentTopic: (topic) => {
    markEdited();
    set((state) => {
      if (state.target.intentTopicSelections.some((t) => t.topicId === topic.topicId)) {
        return state;
      }
      return {
        target: {
          ...state.target,
          intentTopicSelections: [...state.target.intentTopicSelections, topic],
        },
      };
    });
  },
  removeIntentTopic: (topicId) => {
    markEdited();
    set((state) => ({
      target: {
        ...state.target,
        intentTopicSelections: state.target.intentTopicSelections.filter(
          (t) => t.topicId !== topicId,
        ),
      },
    }));
  },
  removeIntentTopicsBySubcategory: (category, subcategory) => {
    markEdited();
    set((state) => ({
      target: {
        ...state.target,
        intentTopicSelections: state.target.intentTopicSelections.filter(
          (t) => !(t.category === category && t.subcategory === subcategory),
        ),
      },
    }));
  },
  removeIntentTopicsByCategory: (category) => {
    markEdited();
    set((state) => ({
      target: {
        ...state.target,
        intentTopicSelections: state.target.intentTopicSelections.filter(
          (t) => t.category !== category,
        ),
      },
    }));
  },
  clearIntentTopics: () => {
    markEdited();
    set((state) => ({ target: { ...state.target, intentTopicSelections: [] } }));
  },

  // Content setters
  addContentFile: (file) => {
    markEdited();
    set((state) => {
      if (state.content.files.some((f) => f.id === file.id)) return state;
      return { content: { ...state.content, files: [...state.content.files, file] } };
    });
  },
  updateContentFile: (id, patch) => {
    markEdited();
    set((state) => ({
      content: {
        ...state.content,
        files: state.content.files.map((f) => (f.id === id ? { ...f, ...patch } : f)),
      },
    }));
  },
  removeContentFile: (id) => {
    markEdited();
    set((state) => ({
      content: { ...state.content, files: state.content.files.filter((f) => f.id !== id) },
    }));
  },

  addContentLink: (link) => {
    markEdited();
    set((state) => ({
      content: {
        ...state.content,
        links: [...state.content.links, { id: cryptoRandomId(), ...link }],
      },
    }));
  },
  updateContentLink: (id, patch) => {
    markEdited();
    set((state) => ({
      content: {
        ...state.content,
        links: state.content.links.map((l) => (l.id === id ? { ...l, ...patch } : l)),
      },
    }));
  },
  removeContentLink: (id) => {
    markEdited();
    set((state) => ({
      content: { ...state.content, links: state.content.links.filter((l) => l.id !== id) },
    }));
  },

  setContentLinks: (links) => set((state) => ({ content: { ...state.content, links } })),
  setContentFiles: (files) => set((state) => ({ content: { ...state.content, files } })),
  // Bulk-set sibling of setContentLinks / setContentFiles — used by both the
  // Save flow AND hydrateStoreFrom(), so it must NOT call markEdited()
  // (hydration from the server would otherwise flag the freshly-loaded draft
  // as user-edited and trigger an echo autosave). Callers that represent a
  // real user action are responsible for marking edited themselves.
  setRequestedContentProducts: (products) =>
    set((state) => ({
      // Dedup preserving first-occurrence order (Set iteration matches
      // insertion order, and matches the BE validator's behaviour).
      content: { ...state.content, requestedProducts: Array.from(new Set(products)) },
    })),
  removeRequestedContentProduct: (kind) => {
    markEdited();
    set((state) => ({
      content: {
        ...state.content,
        requestedProducts: state.content.requestedProducts.filter((k) => k !== kind),
      },
    }));
  },

  // Per-tab unsaved-input flags (NPJB-425): each setter touches only its own
  // slot so the link, upload, and Build Content accordions never clobber each
  // other's dirty state. Cleared on Save/Cancel (per-tab) or wholesale on discard.
  setUnsavedLink: (dirty) => {
    set((state) => ({
      content: { ...state.content, unsavedInput: { ...state.content.unsavedInput, link: dirty } },
    }));
  },
  setUnsavedUpload: (dirty) => {
    set((state) => ({
      content: {
        ...state.content,
        unsavedInput: { ...state.content.unsavedInput, upload: dirty },
      },
    }));
  },
  setUnsavedProduct: (dirty) => {
    set((state) => ({
      content: {
        ...state.content,
        unsavedInput: { ...state.content.unsavedInput, product: dirty },
      },
    }));
  },
  clearUnsavedContent: () => {
    set((state) => ({
      content: { ...state.content, unsavedInput: { link: false, upload: false, product: false } },
    }));
  },

  // Products actions.
  // markEdited() runs BEFORE set() — set() notifies the autosave subscriber
  // synchronously, and the subscriber bails unless hasUserEdited is already
  // true. Marking after set() loses the very first edit (the draft never gets
  // created when the user's first action is on the Products step). Matches the
  // mark-before-set ordering used by every target/content setter above.
  toggleProduct: (product) => {
    markEdited();
    const current = get().products.selectedProducts;
    const exists = current.some((p) => p.id === product.id);
    const next = exists ? current.filter((p) => p.id !== product.id) : [...current, product];
    set((s) => {
      if (exists) {
        const { [product.id]: _removed, ...remainingAnswers } = s.products.productAnswers;
        return {
          ...s,
          products: {
            ...s.products,
            selectedProducts: next,
            productAnswers: remainingAnswers,
          },
        };
      }
      return { ...s, products: { ...s.products, selectedProducts: next } };
    });
  },
  clearProducts: () => {
    markEdited();
    set((s) => ({
      ...s,
      products: { ...s.products, selectedProducts: [], productAnswers: {} },
    }));
  },
  setBudget: (value) => {
    markEdited();
    set((s) => ({ ...s, products: { ...s.products, budget: value } }));
  },
  setSelectedProducts: (products) => {
    markEdited();
    set((s) => {
      const allowedIds = new Set(products.map((p) => p.id));
      const prunedAnswers: Record<string, ProductAnswers> = {};
      for (const [pid, answers] of Object.entries(s.products.productAnswers)) {
        if (allowedIds.has(pid)) prunedAnswers[pid] = answers;
      }
      return {
        ...s,
        products: {
          ...s.products,
          selectedProducts: products,
          productAnswers: prunedAnswers,
        },
      };
    });
  },
  setProductAnswer: (productId, key, value) => {
    markEdited();
    set((s) => {
      const existing = s.products.productAnswers[productId] ?? {};
      const nextAnswers = {
        ...s.products.productAnswers,
        [productId]: { ...existing, [key]: value },
      };
      // Only the goal whose answer changed is recomputed. 5 → 0 or a cleared
      // answer still gives null.
      return aggregateGoalsInto(s, nextAnswers, goalAnswerChanged(key, existing[key], value));
    });
  },
  setProductAnswers: (productAnswers) => {
    markEdited();
    // Draft hydrate path. All-empty goal answers keep the goal the server sent.
    set((s) => aggregateGoalsInto(s, productAnswers, goalAnswersFilled(productAnswers)));
  },
  clearProductAnswers: (productId) => {
    markEdited();
    set((s) => {
      const { [productId]: _removed, ...rest } = s.products.productAnswers;
      return { ...s, products: { ...s.products, productAnswers: rest } };
    });
  },
  clearAllProductAnswers: () => {
    markEdited();
    set((s) => ({ ...s, products: { ...s.products, productAnswers: {} } }));
  },

  setDraftIdentity: ({ draftId, lastSeenUpdatedAt, status, following }) =>
    set({ draftId, lastSeenUpdatedAt, status, draftFollowing: following }),
  setLastSeenUpdatedAt: (lastSeenUpdatedAt) => set({ lastSeenUpdatedAt }),
  setDraftFollowing: (draftFollowing) => set({ draftFollowing }),
  clearDraftIdentity: () =>
    set({
      draftId: null,
      lastSeenUpdatedAt: null,
      status: null,
      campaignId: null,
      draftFollowing: false,
    }),

  // "Clear all files and filters" on the Target summary. It empties the Target step only.
  // The name and the Products-step goals sit in the target slice too, so they are kept.
  // Content and Products are other steps, so they are kept as well (NPJB-847).
  clearAll: () => {
    markEdited();
    set((state) => ({
      target: {
        ...emptyTarget(),
        name: state.target.name,
        leadGoal: state.target.leadGoal,
        campaignTimeframe: state.target.campaignTimeframe,
        startDate: state.target.startDate,
        endDate: state.target.endDate,
        impressionGoal: state.target.impressionGoal,
        dealReference: state.target.dealReference,
      },
      // Forget dropped US states too, or re-adding United States would bring them back.
      clearedStates: null,
      prefilledFromExplore: false,
    }));
  },

  reset: () =>
    set({
      draftId: null,
      lastSeenUpdatedAt: null,
      status: null,
      draftFollowing: false,
      // NPJB-559 — drop the remembered states; a fresh audience starts clean.
      clearedStates: null,
      target: emptyTarget(),
      content: emptyContent(),
      products: emptyProducts(),
      prefilledFromExplore: false,
    }),

  /** Reset identity + content + products for a new session, preserving
   *  target (filters/brief carried from Explore→Campaign handoff). */
  resetKeepingTarget: () =>
    set({
      draftId: null,
      lastSeenUpdatedAt: null,
      status: null,
      campaignId: null,
      draftFollowing: false,
      clearedStates: null,
      content: emptyContent(),
      products: emptyProducts(),
    }),
}));
